<template>
  <div class="bpmn-instances">
    <div class="bpmn-instances__toolbar">
      <div class="bpmn-instances__filter">
        <button
          v-for="f in statusFilters"
          :key="f.value"
          :class="['filter-btn', { 'filter-btn--active': statusFilter === f.value }]"
          @click="statusFilter = f.value"
        >
          {{ f.label }}
          <span class="filter-btn__count">{{ filterCount(f.value) }}</span>
        </button>
      </div>
      <button class="btn-refresh" @click="loadInstances">
        ↻ Обновить
      </button>
    </div>

    <div v-if="instancesLoading" class="bpmn-instances__state">Загрузка...</div>
    <div v-else-if="instancesError" class="bpmn-instances__state bpmn-instances__state--error">
      {{ instancesError }}
    </div>
    <template v-else>
      <div v-if="!filteredInstances.length" class="bpmn-instances__state bpmn-instances__state--empty">
        Процессов не найдено
      </div>
      <table v-else class="bpmn-instances__table">
        <thead>
          <tr>
            <th class="col-id">#</th>
            <th class="col-status">Статус</th>
            <th class="col-schema">Схема</th>
            <th class="col-project">Проект</th>
            <th class="col-block">Блок / Подъезд</th>
            <th class="col-floor">Этаж</th>
            <th class="col-wscg">Группа работ</th>
            <th class="col-pt">Тип пом.</th>
            <th class="col-nodes">Текущий шаг</th>
            <th class="col-date">Запущен</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="inst in filteredInstances"
            :key="inst.process_instance_id"
            class="bpmn-instances__row"
            @click="selectInstance(inst)"
          >
            <td class="col-id">{{ inst.process_instance_id }}</td>
            <td class="col-status">
              <span class="instance-badge" :class="'instance-badge--' + inst.status">
                {{ inst.status === 'active' ? 'Активен' : 'Завершён' }}
              </span>
            </td>
            <td class="col-schema">
              <span class="schema-pill">Схема {{ inst.schema_id }}</span>
            </td>
            <td class="col-project">
              <div class="cell-project-name">{{ inst.project_name || '—' }}</div>
              <div class="cell-sub">ID: {{ inst.project_id }}</div>
            </td>
            <td class="col-block">
              <span v-if="inst.block_name">{{ inst.block_name }}</span>
              <span v-if="inst.entrance" class="cell-sub"> / п.{{ inst.entrance }}</span>
              <span v-if="!inst.block_name && !inst.entrance">—</span>
            </td>
            <td class="col-floor">
              <div>{{ inst.level_num ?? '—' }}</div>
              <div class="cell-sub">ID: {{ inst.floor_map_id }}</div>
            </td>
            <td class="col-wscg">
              <Clickable
                class="cell-wscg-name"
                block-mode
                @click.stop="onWscgClick(inst)"
              >
                {{ inst.work_set_check_group_name || '—' }}
                <q-tooltip>
                  Перейти к схеме группы работ
                </q-tooltip>
              </Clickable>
              <div class="cell-sub">ID: {{ inst.work_set_check_group_id }}</div>
            </td>
            <td class="col-pt"><div>{{ inst.placement_type_name }}
              <q-tooltip>
                ID: {{ inst.placement_type_id }}
              </q-tooltip></div>
            </td>
            <td class="col-nodes">
              <span
                v-if="inst.current_node_labels && inst.current_node_labels.length"
                class="node-labels"
              >
                📍 {{ inst.current_node_labels.join(' · ') }}
              </span>
              <span v-else class="node-labels--done">—</span>
            </td>
            <td class="col-date">{{ formatDate(inst.started_at) }}</td>
          </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import Clickable from '@/components/common/Clickable.vue'
import { wcheckBpmnAPI } from './services/api'
import { PAGES } from '@/constants'

const router = useRouter()

const instances = ref([])
const instancesLoading = ref(false)
const instancesError = ref(null)

const statusFilter = ref('all')
const statusFilters = [
  { value: 'all',       label: 'Все' },
  { value: 'active',    label: 'Активные' },
  { value: 'completed', label: 'Завершённые' },
]

const sortedInstances = computed(() =>
  [...instances.value].sort((a, b) => new Date(b.started_at) - new Date(a.started_at))
)

const filteredInstances = computed(() => {
  if (statusFilter.value === 'all') return sortedInstances.value
  return sortedInstances.value.filter((i) => i.status === statusFilter.value)
})

function filterCount(val) {
  if (val === 'all') return instances.value.length
  return instances.value.filter((i) => i.status === val).length
}

async function loadInstances() {
  instancesLoading.value = true
  instancesError.value = null
  try {
    const res = await wcheckBpmnAPI.getProcessInstances()
    instances.value = res?.data || []
  } catch (e) {
    instancesError.value = e?.response?.data?.error || e?.message || 'Ошибка загрузки'
  } finally {
    instancesLoading.value = false
  }
}

function selectInstance(inst) {
  router.push(`/work-check-bpmn-active/instances/${inst.process_instance_id}`)
}

function onWscgClick(row) {
  const {
    resident_id,
    project_type_id,
    project_entrance_id,
    project_id,
    sbs_project_id,
    floor_map_id,
    level_num,
  } = row
  const q = new URLSearchParams({
    resident_id: String(resident_id),
    project_type_id: String(project_type_id),
    project_entrance_id: String(project_entrance_id),
    project_id: String(project_id),
    sbs_project_id: String(sbs_project_id),
    floor_map_id: String(floor_map_id),
    floor: String(level_num),
    tab: 'EntranceSchema',
  })
  window.open(
    `${PAGES.residentialSettings}?${q.toString()}`,
    '_blank',
    'noopener,noreferrer',
  )
}

function formatDate(dt) {
  if (!dt) return '—'
  return new Date(dt).toLocaleString('ru-RU')
}

onMounted(() => {
  loadInstances()
})
</script>

<style scoped lang="scss">
.bpmn-instances {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.bpmn-instances__toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.bpmn-instances__filter {
  display: flex;
  gap: 4px;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 3px;
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 13px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  color: #546e7a;
  transition: all 0.15s;
  white-space: nowrap;
}
.filter-btn:hover { background: #e0e0e0; }
.filter-btn--active {
  background: #fff;
  color: #1565c0;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.filter-btn__count {
  font-size: 11px;
  font-weight: 700;
  background: #e3f2fd;
  color: #1565c0;
  border-radius: 10px;
  padding: 1px 6px;
  min-width: 18px;
  text-align: center;
}
.filter-btn--active .filter-btn__count {
  background: #1976d2;
  color: #fff;
}

.btn-refresh {
  padding: 6px 14px;
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #1565c0;
  margin-left: auto;
}
.btn-refresh:hover { background: #bbdefb; }

.bpmn-instances__state {
  padding: 40px;
  text-align: center;
  color: #757575;
  font-size: 14px;
}
.bpmn-instances__state--error {
  color: #c62828;
}

.bpmn-instances__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.bpmn-instances__table thead tr {
  background: #f5f7fa;
}

.bpmn-instances__table th {
  padding: 10px 14px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: #546e7a;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-bottom: 2px solid #e0e0e0;
  white-space: nowrap;
}

.bpmn-instances__row {
  cursor: pointer;
  transition: background 0.12s;
  border-bottom: 1px solid #f0f0f0;
}
.bpmn-instances__row:last-child { border-bottom: none; }
.bpmn-instances__row:hover { background: #e8f4fd; }

.bpmn-instances__table td {
  padding: 10px 14px;
  color: #212121;
  vertical-align: middle;
}

.col-id      { width: 46px; color: #9e9e9e; font-size: 12px; }
.col-status  { width: 90px; }
.col-schema  { width: 80px; }
.col-project { width: 160px; }
.col-block   { width: 110px; }
.col-floor   { width: 60px; text-align: center; }
.col-wscg    { width: 180px; }
.col-pt      { width: 70px; text-align: center; }
.col-date    { width: 150px; white-space: nowrap; font-size: 12px; color: #757575; }

.cell-project-name,
.cell-wscg-name {
  font-size: 12px;
  font-weight: 500;
  color: #212121;
  line-height: 1.3;
}
.cell-sub {
  font-size: 11px;
  color: #9e9e9e;
  margin-top: 1px;
}

.instance-badge {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.instance-badge--active    { background: #c8e6c9; color: #2e7d32; }
.instance-badge--completed { background: #e0e0e0; color: #616161; }

.schema-pill {
  display: inline-block;
  padding: 2px 8px;
  background: #e3f2fd;
  color: #1565c0;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}

.node-labels {
  font-size: 12px;
  color: #e65100;
  font-weight: 500;
}
.node-labels--done {
  color: #bdbdbd;
  font-size: 12px;
}
</style>
